import { MouseEvent, useCallback, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import ClassNames from '@streamloots/classnames';
import { ButtonIcon } from 'ui-library/button';
import theme from './image-slider.scss';

const classNames = ClassNames(theme);

export interface ImageSliderProps {
  images: string[];
  initialIndex?: number;
  imageClassName?: string;
  className?: string;
  slideLeftAria?: string;
  slideRightAria?: string;
  hidePagination?: boolean;
  onImageClick?: (index: number) => void;
  onPaginationClick?: (index: number) => void;
  onSlideLeftClick?: (index: number) => void;
  onSlideRightClick?: (index: number) => void;
}

const variants = {
  enter: (direction: number) => {
    return {
      x: direction > 0 ? 1000 : -1000,
      opacity: 0,
    };
  },
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => {
    return {
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0,
    };
  },
};

export const ImageSlider = ({
  images,
  initialIndex = 0,
  imageClassName = '',
  className = '',
  slideLeftAria,
  slideRightAria,
  hidePagination,
  onImageClick,
  onPaginationClick,
  onSlideLeftClick,
  onSlideRightClick,
}: ImageSliderProps): JSX.Element => {
  const [[page, direction], setPage] = useState([initialIndex, 0]);

  const moveLeft = useCallback(() => {
    let newPage = page - 1;
    if (newPage < 0) {
      newPage = images.length - 1;
    }

    if (onSlideLeftClick) {
      onSlideLeftClick(newPage);
    }
    setPage([newPage, -1]);
  }, [images.length, onSlideLeftClick, page]);

  const moveRight = useCallback(() => {
    let newPage = page + 1;
    if (newPage >= images.length) {
      newPage = 0;
    }

    if (onSlideRightClick) {
      onSlideRightClick(newPage);
    }
    setPage([newPage, 1]);
  }, [images.length, onSlideRightClick, page]);

  const swipePower = (offset: number, velocity: number) => {
    return Math.abs(offset) * velocity;
  };

  const handleClick = (event: MouseEvent) => {
    event.stopPropagation();
    if (onImageClick) {
      onImageClick(page);
    }
  };

  const handlePageClick = (pageIndex: number) => {
    if (pageIndex === page) {
      return;
    }
    const newDirection = page > pageIndex ? 1 : -1;

    if (onPaginationClick) {
      onPaginationClick(pageIndex);
    }
    setPage([pageIndex, newDirection]);
  };

  const swipeConfidenceThreshold = 10000;
  const currentItem = images[page];
  const showSliders = images.length > 1;

  return (
    <div className={classNames({ 'image-slider': true, [className]: Boolean(className) })}>
      {showSliders && (
        <ButtonIcon
          icon="chevron-left"
          onClick={event => {
            event.stopPropagation();
            moveLeft();
          }}
          className={classNames({
            'image-slider__button': true,
            'image-slider__button--left': true,
          })}
          ariaLabel={slideLeftAria}
        />
      )}
      <AnimatePresence initial={false} custom={direction}>
        <motion.img
          key={page}
          custom={direction}
          variants={variants}
          src={currentItem}
          initial="enter"
          animate="center"
          exit="exit"
          className={classNames({
            'image-slider__img': true,
            'image-slider__img--click': !!onImageClick,
            [imageClassName]: Boolean(imageClassName),
          })}
          transition={{
            x: { type: 'spring', stiffness: 300, damping: 30 },
            opacity: { duration: 0.2 },
          }}
          drag={showSliders ? 'x' : false}
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={1}
          onDragEnd={(_e, { offset, velocity }) => {
            const swipe = swipePower(offset.x, velocity.x);

            if (swipe < -swipeConfidenceThreshold) {
              moveRight();
            } else if (swipe > swipeConfidenceThreshold) {
              moveLeft();
            }
          }}
          onClick={handleClick}
        />
      </AnimatePresence>
      {showSliders && (
        <ButtonIcon
          icon="chevron-right"
          onClick={event => {
            event.stopPropagation();
            moveRight();
          }}
          className={classNames({
            'image-slider__button': true,
            'image-slider__button--right': true,
          })}
          ariaLabel={slideRightAria}
        />
      )}
      {showSliders && !hidePagination && (
        <div className={classNames('image-slider__pagination')}>
          {images.map((image, index) => (
            <button
              type="button"
              key={index}
              onClick={event => {
                event.stopPropagation();
                handlePageClick(index);
              }}
              className={classNames({
                'image-slider__pagination__button': true,
                'image-slider__pagination__button--selected': index === page,
              })}
              aria-label={image}
            >
              {' '}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
